import { useCallback, useEffect, useMemo, useState } from 'react';
import isNull from 'lodash/isNull';
import { useFormikContext } from 'formik';
import { InvoiceFormData } from 'types/invoice';
import Typography from '@mui/material/Typography';
import numberWithCommas from 'utils/numberWithCommas';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import {
    estimateNewInvoice,
    createNewInvoiceWithoutSquare,
    generateEmailEstimateNewInvoice,
} from 'services/invoiceService';
import { useNavigate, useParams } from 'react-router-dom';
import { BackdropLoader } from 'components/@shared/Loaders';
import { PATH } from 'routes/path';
import Box from '@mui/material/Box';
import { useNotifications } from 'hooks/useNotifications';

export const InvoiceTotalBlock = () => {
    const { values } = useFormikContext<InvoiceFormData>();
    const [isLoading, setIsLoading] = useState(false);
    const [total, setTotal] = useState<null | number>(null);

    const { appointmentId } = useParams<{ appointmentId: string }>();

    const navigate = useNavigate();
    const notifications = useNotifications();

    const handleGeneralEmailEstimate = useCallback(
        (asAppointmentUser: boolean) => {
            if (appointmentId) {
                setIsLoading(true);
                generateEmailEstimateNewInvoice(values, appointmentId, asAppointmentUser)
                    .then(() => notifications.show({ message: 'Email estimate successfully sent' }))
                    .finally(() => setIsLoading(false));
            }
        },
        [values, appointmentId],
    );

    const handleGeneralEmailEstimateToUser = useCallback(() => {
        handleGeneralEmailEstimate(true);
    }, [handleGeneralEmailEstimate]);

    const handleGeneralEmailEstimateToContractor = useCallback(() => {
        handleGeneralEmailEstimate(false);
    }, [handleGeneralEmailEstimate]);

    const handleCompleteWithoutSquare = useCallback(() => {
        if (appointmentId) {
            setIsLoading(true);
            createNewInvoiceWithoutSquare(values, appointmentId)
                .then(() => navigate(PATH.APPOINTMENTS))
                .finally(() => setIsLoading(false));
        }
    }, [values, appointmentId]);

    const disabled = useMemo(() => !values.scheduledPayDate || !values.title, [values]);

    useEffect(() => {
        if (appointmentId) {
            setIsLoading(true);
            estimateNewInvoice(appointmentId)
                .then(setTotal)
                .finally(() => setIsLoading(false));
        }
    }, [appointmentId]);

    return (
        <Stack gap={3}>
            {isLoading && <BackdropLoader />}
            <Typography textAlign="right">
                Total: ${isNull(total) ? '0.00' : `${numberWithCommas(total, 2)}`}
            </Typography>
            <Box display="flex" gap={2} width="100%">
                <Button variant="outlined" disabled={disabled} onClick={handleGeneralEmailEstimateToUser}>
                    Generate Email to User
                </Button>
                <Button variant="outlined" disabled={disabled} onClick={handleGeneralEmailEstimateToContractor}>
                    Generate Email to Contractor
                </Button>
                <Button variant="outlined" disabled={disabled} onClick={handleCompleteWithoutSquare}>
                    Complete payment without Square
                </Button>
            </Box>
        </Stack>
    );
};
